import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Reply } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiPost } from '../../../services/apiClient';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';

/**
 * Props for the ContinueNegotiationDialog component
 */
export interface ContinueNegotiationDialogProps {
    open: boolean;
    vehicleId: string;
    negotiationId: string;  
    messageId: string; /** Message ID from the last negotiation response */
    messagesHistory: string[];
    onClose: () => void;
    onSuccess?: () => void;
}

/**
 * Dialog component for continuing an existing negotiation (used only for testing purposes)
 */
export default function ContinueNegotiationDialog({
  open,
  vehicleId,
  negotiationId,
  messageId,
  messagesHistory,
  onClose
}: ContinueNegotiationDialogProps): JSX.Element {
  const { t } = useTranslation();

  const [emailResponse, setEmailResponse] = useState<string>(t('Thank you for your offer. Can you do a better price?'));
  const [emailSubject, setEmailSubject] = useState<string>(t('Re: Offer for transport'));
  const [chatMessagesHistory, setChatMessagesHistory] = useState<string[]>([...messagesHistory]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  /**
     * Handles continuing the negotiation
     */
  const handleContinueNegotiation = async (): Promise<void> => {
    if (!emailResponse.trim()) {
      setError(t('Email response is required'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const requestBody = {
        email_response: emailResponse.trim(),
        email_id: messageId,
        email_subject: emailSubject.trim()
      };
      messagesHistory.push(`CLIENT: ${emailResponse}`);
      setChatMessagesHistory(messagesHistory);
      setEmailResponse('');
      const response = await apiPost(
        `${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicleId}/negotiation/${negotiationId}/continue`,
        requestBody
      );
      const rbody = await response.json();
      let message = typeof rbody.result.messageSent === 'string'
        ? rbody.result.messageSent
        : JSON.stringify(rbody.result.messageSent);
      if(rbody.result.nextStep === 'need_human'){
        message = `(need human), reason: ${rbody.result.reason}`;
      }
      messagesHistory.push(`AI: ${message || 'Validate'}`);
      setChatMessagesHistory(messagesHistory);
    } catch (error: any) {
      switch (error?.errorData?.error?.errorKey) {
        case 'client.feature.disabled':
          setError(t('This feature is currently disabled.'));
          break;
        case 'negotiation.invalid_step':
          setError(t('Invalid negotiation step.'));
          break;
        default:
          toast.error(t('Something went wrong!'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
     * Handles dialog close
     */
  const handleClose = (): void => {
    if (!isLoading) {
      setError('');
      onClose();
    }
  };
  return (
    <Dialog
      open={open && process.env.REACT_APP_ENV !== 'production'} // Only open in non-production environments for testing
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Reply sx={{ color: 'primary.main' }} />
          <Typography variant="h6">{t('Continue Negotiation')}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('Send a follow-up message to continue the negotiation')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <TextField
            fullWidth
            label={t('Email Subject')}
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleContinueNegotiation()}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: (theme: any) => theme.typography.fontSizes.small
              }
            }}
          />
          <TextField
            fullWidth
            label={t('Email Response')}
            multiline
            rows={4}
            value={emailResponse}
            onChange={(e) => setEmailResponse(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleContinueNegotiation()}
            error={!!error && !emailResponse.trim()}
            helperText={error && !emailResponse.trim() ? t('Email response is required') : t('Your response message to continue the negotiation')}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: (theme: any) => theme.typography.fontSizes.small
              }
            }}
          />
          {error && emailResponse.trim() && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">{t('Messages history')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {chatMessagesHistory.map((message, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    m: 1,
                    backgroundColor: 'background.lightGrey',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {typeof message === 'string' ? message : ''}
                  </Typography>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
          <Box
            sx={{
              p: 1,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('Negotiation Details:')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Vehicle ID:')} {vehicleId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Negotiation ID:')} {negotiationId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Message ID:')} {messageId}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          color="inherit"
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleContinueNegotiation}
          variant="contained"
          disabled={isLoading || !emailResponse.trim()}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Reply />}
          sx={{
            backgroundColor: 'background.orange',
            '&:hover': { backgroundColor: 'background.orangeDark' }
          }}
        >
          {isLoading ? t('Sending...') : t('Continue Negotiation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
